/**
 * components/Grid/HairlineGrid
 * ──────────────────────────────────────────────────────────────────────────
 * The hairline-divided grid: cells laid out with a 1px gap over a #F2F2F2
 * background, so the background shows through as the dividing rule.
 *
 * This is how the design draws the reconciliation legs (Expected / Counted /
 * Variance), the read-only policy quad and the numbered steps in the
 * educational panels. Using the gap as the rule — rather than borders on each
 * cell — is why there is never a doubled 2px line where two cells meet, and
 * why the outer edge has no stray rule.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Children, useState, type ReactNode } from 'react';
import { StyleSheet, View, type LayoutChangeEvent, type ViewStyle } from 'react-native';
import { neutral } from '../../theme/colors';
import { autoFitColumns } from '../../lib/responsive';

export interface HairlineGridProps {
  children: ReactNode;
  /** The `minmax()` minimum — 150px for reconciliation legs. */
  min: number;
  /** Fixed column count, when the design specifies one (the policy quad: 4). */
  columns?: number;
  style?: ViewStyle;
}

export function HairlineGrid({ children, min, columns, style }: HairlineGridProps) {
  const [available, setAvailable] = useState(0);

  const onLayout = (event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.width);
    if (next !== available) setAvailable(next);
  };

  const items = Children.toArray(children).filter(Boolean);

  // A fixed column count still has to reflow on a phone: four 150px cells do
  // not fit in 360px, so the auto-fit result wins when it is smaller.
  const fitted =
    available > 0
      ? autoFitColumns(available, min, 1, items.length)
      : { count: 1, width: available };
  const count = columns ? Math.min(columns, fitted.count) : fitted.count;
  const width = available > 0 ? (available - (count - 1)) / count : undefined;

  return (
    <View style={[styles.grid, style]} onLayout={onLayout}>
      {items.map((child, index) => (
        <View key={index} style={width !== undefined ? { width } : styles.fullWidth}>
          {child}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    // The 1px gap over this fill IS the rule.
    gap: 1,
    backgroundColor: neutral.ruleLight,
  },
  fullWidth: { width: '100%' },
});

export default HairlineGrid;
